import React from "react"
import { Helmet } from "react-helmet"
import Layout from "../../../components/Layout"
import Header from "../../../components/Header"
import {
  HowItWorksHero,
  TableOfContents,
  WhatPremagicDoes,
  TwoHalves,
  EndToEndJourney,
  DataIntegration,
  Widgets,
  EmailFromYourDomain,
  SuccessOutcomes,
  HowItWorksCTA,
} from "../../../components/integrations/HowItWorks"
import ThumbnailImage from "../../../images/thumbnails/premagic-web-square.png"

const TITLE =
  "How Premagic Integrates with Your Event Platform | Premagic"
const DESCRIPTION =
  "A plain-English customer guide to the Premagic integration: what gets synced from your event platform, what attendees see, and what widgets you can embed where."
const URL = "https://premagic.com/integrations/how-it-works/"

const HowItWorksPage: React.FC = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "How Premagic Integrates with Your Event Platform",
    description: DESCRIPTION,
    url: URL,
    author: { "@type": "Organization", name: "Premagic" },
    publisher: {
      "@type": "Organization",
      name: "Premagic",
      logo: {
        "@type": "ImageObject",
        url: "https://premagic.com/static/premagic-logo.png",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
  }

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to integrate Premagic with your event platform",
    description:
      "End-to-end steps to connect your event registration platform to Premagic, sync attendees, and embed Premagic widgets.",
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Connect your platform",
        text:
          "From inside the Premagic dashboard, link your event platform with a few clicks.",
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "Backfill registrations",
        text:
          "Premagic imports your existing registration list in one pass, including any registrations that came in before you connected.",
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Subscribe to live updates",
        text:
          "Webhooks subscribe Premagic to new registrations, edits and cancellations within seconds.",
      },
      {
        "@type": "HowToStep",
        position: 4,
        name: "Create campaigns",
        text:
          "Activate photo delivery, advocacy posters and speaker amplification against any audience slice.",
      },
      {
        "@type": "HowToStep",
        position: 5,
        name: "Write back to your platform",
        text:
          "Each attendee's gallery link, advocacy poster link and face ID are saved as custom fields in your event platform.",
      },
      {
        "@type": "HowToStep",
        position: 6,
        name: "Embed Premagic widgets",
        text:
          "Embed Quick Register, Poster Creator, the My Photos iframe and the Live Photo Slider on your platform pages.",
      },
      {
        "@type": "HowToStep",
        position: 7,
        name: "Optionally send email from your own domain",
        text:
          "Add SPF, DKIM and DMARC DNS records so Premagic outbound mail is sent from your domain.",
      },
    ],
  }

  const breadcrumbs = [
    { name: "Home", url: "https://premagic.com/" },
    { name: "Integrations", url: "https://premagic.com/integrations/" },
    { name: "How it works", url: URL },
  ]

  return (
    <Layout
      title={TITLE}
      description={DESCRIPTION}
      keywords={[
        "premagic integration",
        "event platform integration",
        "attendee data sync",
        "event widgets",
        "swapcard integration",
        "luma integration",
        "cvent integration",
        "hubspot events",
        "event registration api",
        "personal photo gallery iframe",
      ]}
      metaImage={ThumbnailImage}
      breadcrumbs={breadcrumbs}
    >
      <Helmet>
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>
      <Header />
      <HowItWorksHero />
      <TableOfContents />
      <WhatPremagicDoes />
      <TwoHalves />
      <EndToEndJourney />
      <DataIntegration />
      <Widgets />
      <EmailFromYourDomain />
      <SuccessOutcomes />
      <HowItWorksCTA />
    </Layout>
  )
}

export default HowItWorksPage
